<template>
  <div class="admin-analytics">
    <div class="bg-gradient"></div>
    <div class="bg-blob blob-1"></div>
    <div class="bg-blob blob-2"></div>

    <header class="top-bar glass-panel">
      <router-link to="/dashboard" class="top-logo">
        <AppLogo class="logo-img" />
      </router-link>
      <div class="top-actions">
        <router-link to="/dashboard" class="back-link">
          <i class="fas fa-arrow-left"></i>
          Dashboard
        </router-link>
        <button type="button" class="btn-refresh" :disabled="loading" @click="loadAll">
          <i class="fas fa-sync-alt" :class="{ 'fa-spin': loading }"></i>
          Refresh
        </button>
      </div>
    </header>

    <div class="page-intro">
      <h1>Analytics</h1>
      <p class="subtitle">Usage metrics, follow-ups, and resume quality review</p>
    </div>

    <nav class="admin-tabs glass-panel">
      <button
        v-for="tab in tabs"
        :key="tab.id"
        type="button"
        class="admin-tab"
        :class="{ active: activeTab === tab.id }"
        @click="setTab(tab.id)"
      >
        {{ tab.label }}
      </button>
    </nav>

    <p v-if="error" class="error-banner">{{ error }}</p>

    <div v-if="loading && !overview" class="loading-state">
      <i class="fas fa-spinner fa-spin"></i>
      <span>Loading analytics…</span>
    </div>

    <template v-if="activeTab === 'overview'">
    <section v-if="overview" class="investor-strip glass-panel">
      <div class="strip-item">
        <span class="strip-value">{{ overview.users.total }}</span>
        <span class="strip-label">Registered users</span>
      </div>
      <div class="strip-divider"></div>
      <div class="strip-item">
        <span class="strip-value">{{ overview.users.activationRate }}%</span>
        <span class="strip-label">Activation (resume uploaded)</span>
      </div>
      <div class="strip-divider"></div>
      <div class="strip-item">
        <span class="strip-value">{{ overview.engagement.resumesGenerated }}</span>
        <span class="strip-label">AI resumes generated</span>
      </div>
      <div class="strip-divider"></div>
      <div class="strip-item">
        <span class="strip-value">{{ overview.funnel.appliedOrBeyond }}</span>
        <span class="strip-label">Applications tracked</span>
      </div>
    </section>

    <section v-if="overview" class="kpi-grid">
      <div class="kpi-card glass-panel" v-for="kpi in kpiCards" :key="kpi.label">
        <span class="kpi-value">{{ kpi.value }}</span>
        <span class="kpi-label">{{ kpi.label }}</span>
        <span v-if="kpi.hint" class="kpi-hint">{{ kpi.hint }}</span>
      </div>
    </section>

    <div v-if="overview" class="charts-row">
      <section class="panel glass-panel">
        <h2>Weekly signups</h2>
        <div class="bar-chart">
          <div
            v-for="bar in growthBars.signups"
            :key="'su-' + bar.week"
            class="bar-col"
            :title="`${bar.week}: ${bar.count}`"
          >
            <div class="bar-fill signups" :style="{ height: bar.height + '%' }"></div>
            <span class="bar-label">{{ bar.label }}</span>
          </div>
        </div>
      </section>

      <section class="panel glass-panel">
        <h2>Resume generations / week</h2>
        <div class="bar-chart">
          <div
            v-for="bar in growthBars.generations"
            :key="'gen-' + bar.week"
            class="bar-col"
            :title="`${bar.week}: ${bar.count}`"
          >
            <div class="bar-fill generations" :style="{ height: bar.height + '%' }"></div>
            <span class="bar-label">{{ bar.label }}</span>
          </div>
        </div>
      </section>
    </div>

    <div v-if="overview" class="detail-row">
      <section class="panel glass-panel">
        <h2>Application funnel</h2>
        <ul class="funnel-list">
          <li v-for="item in funnelItems" :key="item.status">
            <span class="funnel-status">{{ item.label }}</span>
            <span class="funnel-count">{{ item.count }}</span>
            <div class="funnel-bar-wrap">
              <div class="funnel-bar" :style="{ width: item.pct + '%' }"></div>
            </div>
          </li>
        </ul>
        <p class="panel-note">
          {{ overview.funnel.appliedOrBeyond }} users marked applied or beyond
          <span v-if="overview.funnel.applicationRate">({{ overview.funnel.applicationRate }}% of generations)</span>
        </p>
      </section>

      <section class="panel glass-panel">
        <h2>Job sources</h2>
        <ul class="source-list">
          <li v-for="src in overview.topJobSources" :key="src.source">
            <span>{{ src.source }}</span>
            <strong>{{ src.count }}</strong>
          </li>
          <li v-if="!overview.topJobSources.length" class="empty">No source data yet</li>
        </ul>
        <h3 class="sub-heading">Referrals</h3>
        <ul class="source-list compact">
          <li v-for="(count, status) in overview.referrals.byStatus" :key="status">
            <span>{{ status }}</span>
            <strong>{{ count }}</strong>
          </li>
          <li>
            <span>Paid conversions</span>
            <strong>{{ overview.referrals.paidConversions }}</strong>
          </li>
        </ul>
      </section>
    </div>
    </template>

    <section v-if="activeTab === 'success'" class="panel glass-panel candidates-panel">
      <div class="candidates-header">
        <div>
          <h2>Success story candidates</h2>
          <p class="panel-desc">Users to contact for testimonials — sorted by outcomes and engagement</p>
        </div>
        <div class="candidates-actions">
          <input
            v-model="candidateSearch"
            type="search"
            placeholder="Search name or email..."
            class="search-input glass-input"
            @keyup.enter="loadCandidates"
          />
          <select v-model="minResumes" class="filter-select glass-input" @change="loadCandidates">
            <option :value="1">1+ resumes</option>
            <option :value="2">2+ resumes</option>
            <option :value="3">3+ resumes</option>
            <option :value="5">5+ resumes</option>
          </select>
          <button type="button" class="btn-export" @click="exportCsv" :disabled="!candidates.length">
            <i class="fas fa-download"></i>
            Export CSV
          </button>
        </div>
      </div>

      <div class="table-wrap">
        <table class="candidates-table">
          <thead>
            <tr>
              <th>Priority</th>
              <th>Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Resumes</th>
              <th>Applied</th>
              <th>Interview</th>
              <th>Offers</th>
              <th>Last active</th>
              <th>Sample roles</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loadingCandidates">
              <td colspan="10" class="loading-cell">Loading...</td>
            </tr>
            <tr v-else-if="!candidates.length">
              <td colspan="10" class="loading-cell">No candidates yet — check back as users generate resumes</td>
            </tr>
            <tr
              v-for="c in candidates"
              :key="c.userId"
              class="clickable-row"
              @click="openUserDetail(c.userId)"
            >
              <td>
                <span class="priority-badge" :class="c.followUpPriority">{{ c.followUpPriority }}</span>
              </td>
              <td>{{ fullName(c) }}</td>
              <td><a :href="'mailto:' + c.email">{{ c.email }}</a></td>
              <td>{{ c.phone || '—' }}</td>
              <td>{{ c.resumesGenerated }}</td>
              <td>{{ c.appliedCount }}</td>
              <td>{{ c.interviewingCount }}</td>
              <td>{{ c.offerCount }}</td>
              <td>{{ formatDate(c.lastJobActivity || c.lastResumeGenerated) }}</td>
              <td class="titles-cell">{{ (c.sampleJobTitles || []).join(', ') || '—' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <section v-if="activeTab === 'inactive'" class="panel glass-panel candidates-panel">
      <div class="candidates-header">
        <div>
          <h2>No resume uploaded</h2>
          <p class="panel-desc">Follow up with signups who haven&apos;t uploaded a base resume yet</p>
        </div>
        <div class="candidates-actions">
          <input
            v-model="inactiveSearch"
            type="search"
            placeholder="Search name or email..."
            class="search-input glass-input"
            @keyup.enter="loadInactive"
          />
          <button type="button" class="btn-export" @click="loadInactive">
            <i class="fas fa-search"></i>
            Search
          </button>
          <button type="button" class="btn-export" @click="exportInactiveCsv" :disabled="!inactiveUsers.length">
            <i class="fas fa-download"></i>
            Export CSV
          </button>
        </div>
      </div>
      <div class="table-wrap">
        <table class="candidates-table">
          <thead>
            <tr>
              <th>Priority</th>
              <th>Name</th>
              <th>Email</th>
              <th>Days since signup</th>
              <th>Last login</th>
              <th>Credits</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loadingInactive">
              <td colspan="6" class="loading-cell">Loading...</td>
            </tr>
            <tr v-else-if="!inactiveUsers.length">
              <td colspan="6" class="loading-cell">Everyone has uploaded a resume — nice!</td>
            </tr>
            <tr
              v-for="u in inactiveUsers"
              :key="u.authUserId"
              class="clickable-row"
              @click="openUserDetail(u.userId || u.authUserId)"
            >
              <td><span class="priority-badge" :class="u.followUpPriority">{{ u.followUpPriority }}</span></td>
              <td>{{ fullName(u) }}</td>
              <td><a :href="'mailto:' + u.email" @click.stop>{{ u.email }}</a></td>
              <td>{{ u.daysSinceSignup }}</td>
              <td>{{ formatDate(u.lastLogin) }}</td>
              <td>{{ u.credits }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <section v-if="activeTab === 'users'" class="panel glass-panel candidates-panel">
      <div class="candidates-header">
        <div>
          <h2>User directory</h2>
          <p class="panel-desc">Search any user — click a row to review jobs, match scores, and grant credits</p>
        </div>
        <div class="candidates-actions">
          <input
            v-model="userSearch"
            type="search"
            placeholder="Search name or email..."
            class="search-input glass-input"
            @keyup.enter="loadUsers"
          />
          <button type="button" class="btn-export" @click="loadUsers">
            <i class="fas fa-search"></i>
            Search
          </button>
        </div>
      </div>
      <div class="table-wrap">
        <table class="candidates-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Resume</th>
              <th>Credits</th>
              <th>Jobs</th>
              <th>Generated</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loadingUsers">
              <td colspan="6" class="loading-cell">Loading...</td>
            </tr>
            <tr v-else-if="!directoryUsers.length">
              <td colspan="6" class="loading-cell">Search for a user by name or email</td>
            </tr>
            <tr
              v-for="u in directoryUsers"
              :key="u.authUserId"
              class="clickable-row"
              @click="openUserDetail(u.userId || u.authUserId)"
            >
              <td>{{ fullName(u) }}</td>
              <td>{{ u.email }}</td>
              <td>{{ u.hasResume ? 'Yes' : 'No' }}</td>
              <td>{{ u.credits }}</td>
              <td>{{ u.jobCount }}</td>
              <td>{{ u.resumesGenerated }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <AdminUserDetailPanel
      :open="detailOpen"
      :user-id="detailUserId"
      @close="detailOpen = false"
    />

    <p v-if="overview?.generatedAt && activeTab === 'overview'" class="footer-meta">
      Data as of {{ formatDateTime(overview.generatedAt) }}
    </p>
  </div>
</template>

<script>
import axios from '@/axios';
import { formatApiErrorMessage } from '@/axios';
import AppLogo from '@/components/AppLogo.vue';
import AdminUserDetailPanel from '@/components/AdminUserDetailPanel.vue';

const FUNNEL_LABELS = {
  saved: 'Saved',
  resume_generated: 'Resume ready',
  applied: 'Applied',
  interviewing: 'Interview',
  offer: 'Offer',
  rejected: 'Passed',
};

export default {
  name: 'AdminAnalytics',
  components: { AppLogo, AdminUserDetailPanel },
  data() {
    return {
      activeTab: 'overview',
      tabs: [
        { id: 'overview', label: 'Overview' },
        { id: 'success', label: 'Success stories' },
        { id: 'inactive', label: 'No resume' },
        { id: 'users', label: 'Users' },
      ],
      loading: false,
      loadingCandidates: false,
      loadingInactive: false,
      loadingUsers: false,
      error: null,
      overview: null,
      growth: null,
      candidates: [],
      inactiveUsers: [],
      directoryUsers: [],
      candidateSearch: '',
      inactiveSearch: '',
      userSearch: '',
      minResumes: 1,
      detailOpen: false,
      detailUserId: null,
    };
  },
  computed: {
    kpiCards() {
      if (!this.overview) return [];
      const u = this.overview.users;
      const e = this.overview.engagement;
      return [
        { label: 'Total users', value: u.total, hint: `+${u.signups30d} last 30d` },
        { label: 'Monthly active', value: u.mau, hint: 'Logged in last 30 days' },
        { label: 'Activation rate', value: u.activationRate + '%', hint: `${u.withBaseResume} uploaded resume` },
        { label: 'Resumes generated', value: e.resumesGenerated, hint: `${e.usersWhoGenerated} users` },
        { label: 'Avg resumes / user', value: e.avgResumesPerActiveUser, hint: 'Among generators' },
        { label: 'Jobs tracked', value: e.totalJobsSaved, hint: `${e.jobsWithTailoredResume} tailored` },
        {
          label: 'Avg match score',
          value: e.avgCompatibilityScore != null ? e.avgCompatibilityScore : '—',
          hint: 'Compatibility AI',
        },
        {
          label: 'Paid referrals',
          value: this.overview.referrals.paidConversions,
          hint: 'Qualified purchases',
        },
      ];
    },
    funnelItems() {
      if (!this.overview) return [];
      const byStatus = this.overview.funnel.byStatus || {};
      const max = Math.max(...Object.values(byStatus), 1);
      return Object.entries(byStatus)
        .sort((a, b) => (b[1] || 0) - (a[1] || 0))
        .map(([status, count]) => ({
          status,
          label: FUNNEL_LABELS[status] || status,
          count,
          pct: Math.round((count / max) * 100),
        }));
    },
    growthBars() {
      const toBars = (series) => {
        const items = series || [];
        const max = Math.max(...items.map((i) => i.count), 1);
        return items.map((item) => ({
          ...item,
          height: Math.max(4, Math.round((item.count / max) * 100)),
          label: item.week.slice(5),
        }));
      };
      return {
        signups: toBars(this.growth?.signups),
        generations: toBars(this.growth?.resumeGenerations),
      };
    },
  },
  mounted() {
    this.loadAll();
  },
  methods: {
    setTab(id) {
      this.activeTab = id;
      if (id === 'success' && !this.candidates.length) this.loadCandidates();
      if (id === 'inactive' && !this.inactiveUsers.length) this.loadInactive();
      if (id === 'users' && !this.directoryUsers.length && this.userSearch.trim()) {
        this.loadUsers();
      }
    },
    openUserDetail(userId) {
      if (!userId) return;
      this.detailUserId = String(userId);
      this.detailOpen = true;
    },
    async loadAll() {
      this.loading = true;
      this.error = null;
      try {
        const [overviewRes, growthRes] = await Promise.all([
          axios.get('/api/admin/analytics/overview'),
          axios.get('/api/admin/analytics/growth', { params: { weeks: 12 } }),
        ]);
        this.overview = overviewRes.data;
        this.growth = growthRes.data;
      } catch (err) {
        this.error = formatApiErrorMessage(err, 'Could not load analytics');
      } finally {
        this.loading = false;
      }
    },
    async loadInactive() {
      this.loadingInactive = true;
      try {
        const res = await axios.get('/api/admin/users/inactive', {
          params: { limit: 200, search: this.inactiveSearch.trim() },
        });
        this.inactiveUsers = res.data.users || [];
      } catch (err) {
        this.error = formatApiErrorMessage(err, 'Could not load inactive users');
      } finally {
        this.loadingInactive = false;
      }
    },
    async loadUsers() {
      this.loadingUsers = true;
      try {
        const res = await axios.get('/api/admin/users', {
          params: { limit: 100, search: this.userSearch.trim() },
        });
        this.directoryUsers = res.data.users || [];
      } catch (err) {
        this.error = formatApiErrorMessage(err, 'Could not search users');
      } finally {
        this.loadingUsers = false;
      }
    },
    async loadCandidates() {
      this.loadingCandidates = true;
      try {
        const res = await axios.get('/api/admin/analytics/success-candidates', {
          params: {
            limit: 200,
            minResumes: this.minResumes,
            search: this.candidateSearch.trim(),
          },
        });
        this.candidates = res.data.candidates || [];
      } catch (err) {
        this.error = formatApiErrorMessage(err, 'Could not load candidates');
      } finally {
        this.loadingCandidates = false;
      }
    },
    fullName(c) {
      const name = [c.firstName, c.lastName].filter(Boolean).join(' ').trim();
      return name || '—';
    },
    formatDate(iso) {
      if (!iso) return '—';
      return new Date(iso).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    },
    formatDateTime(iso) {
      if (!iso) return '';
      return new Date(iso).toLocaleString();
    },
    exportCsv() {
      const headers = [
        'Priority',
        'First Name',
        'Last Name',
        'Email',
        'Phone',
        'Headline',
        'Resumes Generated',
        'Applied',
        'Interviewing',
        'Offers',
        'Last Active',
        'Signup Date',
        'Sample Jobs',
      ];
      const rows = this.candidates.map((c) => [
        c.followUpPriority,
        c.firstName,
        c.lastName,
        c.email,
        c.phone || '',
        c.headline || '',
        c.resumesGenerated,
        c.appliedCount,
        c.interviewingCount,
        c.offerCount,
        this.formatDate(c.lastJobActivity || c.lastResumeGenerated),
        this.formatDate(c.signupDate),
        (c.sampleJobTitles || []).join('; '),
      ]);
      const escape = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
      const csv = [headers, ...rows].map((row) => row.map(escape).join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `resymaid-success-candidates-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    },
    exportInactiveCsv() {
      const headers = ['Priority', 'First Name', 'Last Name', 'Email', 'Days Since Signup', 'Last Login', 'Credits'];
      const rows = this.inactiveUsers.map((u) => [
        u.followUpPriority,
        u.firstName || '',
        u.lastName || '',
        u.email,
        u.daysSinceSignup,
        this.formatDate(u.lastLogin),
        u.credits,
      ]);
      const escape = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
      const csv = [headers, ...rows].map((row) => row.map(escape).join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `resymaid-inactive-users-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    },
  },
};
</script>

<style scoped>
.admin-analytics {
  min-height: 100vh;
  padding: 0 24px 24px;
  max-width: 1400px;
  margin: 0 auto;
  position: relative;
  color: var(--text-primary);
}

.bg-gradient {
  position: fixed;
  inset: 0;
  background: var(--app-bg);
  z-index: -2;
}

.bg-blob {
  position: fixed;
  border-radius: 50%;
  filter: blur(80px);
  opacity: 0.5;
  z-index: -1;
  pointer-events: none;
}

.blob-1 {
  width: 420px;
  height: 420px;
  top: -120px;
  right: -80px;
  background: var(--blob-1);
}

.blob-2 {
  width: 360px;
  height: 360px;
  bottom: 10%;
  left: -100px;
  background: var(--blob-2);
}

.top-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  margin: 0 -24px 24px;
  border-radius: 0;
  border-left: none;
  border-right: none;
  border-top: none;
}

.top-logo {
  display: flex;
  align-items: center;
  text-decoration: none;
}

.logo-img {
  height: 24px;
  width: auto;
}

.top-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.back-link {
  color: var(--text-secondary);
  text-decoration: none;
  font-size: 14px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 8px;
}

.back-link:hover {
  color: var(--primary);
  background: var(--app-surface-hover);
}

.page-intro {
  margin-bottom: 20px;
}

.page-intro h1 {
  margin: 0;
  font-size: 28px;
  font-weight: 700;
}

.subtitle {
  margin: 6px 0 0;
  color: var(--text-secondary);
  font-size: 14px;
}

.loading-state {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 48px;
  color: var(--text-muted);
}

.investor-strip {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-around;
  gap: 16px;
  padding: 20px 24px;
  margin-bottom: 24px;
  border-radius: 16px;
}

.strip-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  min-width: 120px;
}

.strip-value {
  font-size: 32px;
  font-weight: 700;
  color: var(--primary);
  line-height: 1.1;
}

.strip-label {
  font-size: 12px;
  color: var(--text-secondary);
  margin-top: 4px;
  max-width: 140px;
}

.strip-divider {
  width: 1px;
  height: 48px;
  background: var(--border);
}

.btn-refresh {
  padding: 10px 16px;
  border-radius: 10px;
  border: 1px solid var(--border);
  background: var(--surface-2);
  color: var(--text-primary);
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
}

.btn-refresh:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.error-banner {
  background: var(--danger-subtle);
  border: 1px solid var(--danger);
  color: var(--danger);
  padding: 12px 16px;
  border-radius: 10px;
  margin-bottom: 20px;
}

.kpi-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
}

.kpi-card {
  padding: 20px;
  border-radius: 14px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.kpi-value {
  font-size: 28px;
  font-weight: 700;
  line-height: 1.1;
}

.kpi-label {
  font-size: 13px;
  color: var(--text-secondary);
}

.kpi-hint {
  font-size: 11px;
  color: var(--text-muted);
  margin-top: 4px;
}

.charts-row,
.detail-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 24px;
}

.panel {
  padding: 20px 24px;
  border-radius: 16px;
}

.panel h2 {
  margin: 0 0 16px;
  font-size: 18px;
}

.sub-heading {
  margin: 20px 0 10px;
  font-size: 14px;
  color: var(--text-secondary);
}

.panel-note,
.panel-desc {
  font-size: 13px;
  color: var(--text-secondary);
  margin: 12px 0 0;
}

.bar-chart {
  display: flex;
  align-items: flex-end;
  gap: 6px;
  height: 140px;
  padding-top: 8px;
}

.bar-col {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
  justify-content: flex-end;
  min-width: 0;
}

.bar-fill {
  width: 100%;
  max-width: 28px;
  border-radius: 4px 4px 0 0;
  min-height: 4px;
  transition: height 0.3s ease;
}

.bar-fill.signups {
  background: linear-gradient(180deg, var(--primary-hover), var(--primary));
}

.bar-fill.generations {
  background: linear-gradient(180deg, var(--success), #059669);
}

.bar-label {
  font-size: 9px;
  color: var(--text-muted);
  margin-top: 6px;
  transform: rotate(-45deg);
  white-space: nowrap;
}

.funnel-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.funnel-list li {
  display: grid;
  grid-template-columns: 120px 48px 1fr;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
  font-size: 13px;
}

.funnel-bar-wrap {
  height: 8px;
  background: var(--surface-3);
  border-radius: 4px;
  overflow: hidden;
}

.funnel-bar {
  height: 100%;
  background: var(--primary);
  border-radius: 4px;
}

.source-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.source-list li {
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid var(--border);
  font-size: 14px;
}

.source-list.compact li {
  padding: 6px 0;
  font-size: 13px;
}

.source-list .empty {
  color: var(--text-muted);
  justify-content: center;
}

.candidates-panel {
  margin-bottom: 24px;
}

.candidates-header {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
}

.candidates-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
}

.search-input,
.filter-select {
  padding: 10px 14px;
  border-radius: 10px;
  border: 1px solid var(--input-border);
  background: var(--input-bg);
  color: var(--text-primary);
  font-size: 14px;
}

.search-input {
  min-width: 200px;
}

.btn-export {
  padding: 10px 14px;
  border-radius: 10px;
  border: none;
  background: var(--primary);
  color: var(--primary-foreground);
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
}

.btn-export:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.table-wrap {
  overflow-x: auto;
}

.candidates-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.candidates-table th,
.candidates-table td {
  padding: 10px 12px;
  text-align: left;
  border-bottom: 1px solid var(--border);
}

.candidates-table th {
  color: var(--text-secondary);
  font-weight: 600;
  white-space: nowrap;
}

.candidates-table a {
  color: var(--primary-hover);
}

.titles-cell {
  max-width: 200px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.loading-cell {
  text-align: center;
  color: var(--text-muted);
  padding: 24px !important;
}

.priority-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  text-transform: uppercase;
  font-weight: 600;
}

.priority-badge.high {
  background: color-mix(in srgb, var(--success) 20%, transparent);
  color: var(--success);
}

.priority-badge.medium {
  background: color-mix(in srgb, #eab308 20%, transparent);
  color: #ca8a04;
}

.priority-badge.low {
  background: var(--surface-3);
  color: var(--text-muted);
}

.footer-meta {
  text-align: center;
  font-size: 12px;
  color: var(--text-muted);
  padding-bottom: 32px;
}

.admin-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 8px;
  margin-bottom: 20px;
  border-radius: 12px;
}

.admin-tab {
  padding: 10px 16px;
  border-radius: 8px;
  border: none;
  background: transparent;
  color: var(--text-secondary);
  cursor: pointer;
  font-size: 14px;
}

.admin-tab.active {
  background: var(--primary);
  color: var(--primary-foreground);
}

.clickable-row {
  cursor: pointer;
}

.clickable-row:hover {
  background: var(--app-surface-hover);
}

.glass-panel {
  background: var(--surface-2);
  border: 1px solid var(--border);
  backdrop-filter: blur(12px);
}

@media (max-width: 900px) {
  .strip-divider {
    display: none;
  }

  .investor-strip {
    display: grid;
    grid-template-columns: 1fr 1fr;
  }

  .top-bar {
    margin: 0 -16px 20px;
  }
  .charts-row,
  .detail-row {
    grid-template-columns: 1fr;
  }

  .admin-analytics {
    padding: 0 16px 16px;
  }

  .kpi-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}
</style>
